import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { motion } from 'framer-motion';
import { 
  TrashIcon, CreditCardIcon, DocumentTextIcon, ClockIcon, 
  ShieldCheckIcon, ExclamationTriangleIcon, CheckCircleIcon,
  DocumentArrowUpIcon, EyeIcon
} from '@heroicons/react/24/outline';
import DocumentUpload from '../components/DocumentUpload';
import DocumentCard from '../components/DocumentCard';
import DashboardBackground from '../components/DashboardBackground';
import { toast } from 'react-toastify';
import api from '../services/api';

const GlassCard = ({ children, className = "" }) => (
  <div className={`backdrop-blur-xl bg-white/70 rounded-2xl border border-white/20 shadow-xl ${className}`}>
    {children}
  </div>
);

const Dashboard = () => {
  const { user: authUser, logout } = useContext(AuthContext);
  const [user, setUser] = useState(null);
  const [docs, setDocs] = useState([]);
  const [audit, setAudit] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [uRes, dRes, aRes] = await Promise.all([
          api.get('/api/user/me'),
          api.get('/api/user-docs'),
          api.get('/api/audit-summary'),
        ]);
        if (uRes.data.success) setUser(uRes.data.user);
        setDocs(dRes.data);
        setAudit(aRes.data);
      } catch (err) {
        console.error(err);
      }
    };
    
    // Only fetch data if user is authenticated
    if (authUser) {
      fetchData();
    }
  }, [authUser]);

  const refreshDocs = () => {
    api.get('/api/user-docs')
      .then((r) => setDocs(r.data));
  };

  const deleteAllDocuments = async () => {
    if (!window.confirm('Are you sure you want to delete all documents? This action cannot be undone.')) {
      return;
    }
    
    setDeleting(true);
    try {
      await api.delete('/api/user/documents');
      toast.success('All documents deleted successfully');
      setDocs([]);
      // Refresh audit score
      const aRes = await api.get('/api/audit-summary?recalc=true');
      setAudit(aRes.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete documents');
    } finally {
      setDeleting(false);
    }
  };

  if (!user) return <div className="py-12 text-center">Loading...</div>;

  const handlePay = async () => {
    try {
      const res = await api.post('/api/create-checkout-session', {
        planName: user.planName,
        monthlyPrice: user.monthlyPrice,
      });
      window.location.href = res.data.checkoutUrl;
    } catch (err) {
      console.error(err);
      toast.error('Failed to initiate payment');
    }
  };

  // Required documents for review
  const requiredDocuments = [
    { name: "Form 1040 (Personal Tax Return)", description: "Your most recent personal tax return" },
    { name: "Form 1120/1120S (Business Tax Return)", description: "Your business tax return if applicable" },
    { name: "W-2 Forms", description: "All W-2 forms from employers" },
    { name: "1099 Forms", description: "All 1099 forms (contractor income, interest, etc.)" },
    { name: "Bank Statements", description: "Last 12 months of business bank statements" },
    { name: "Receipts & Invoices", description: "Business expense receipts and invoices" },
    { name: "Payroll Records", description: "If you have employees" },
    { name: "Asset Documentation", description: "Real estate, vehicle, equipment documentation" }
  ];

  const isPendingReview = user.subscriptionStatus === 'pending_review';
  const canPay = user.subscriptionStatus === 'review_complete' && user.monthlyPrice && user.planName;

  return (
    <>
      <DashboardBackground />
      <Navbar />
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Welcome Section with Quick Stats */}
        <GlassCard className="p-8 mb-8">
          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Welcome back, {user.businessName}</h1>
              <p className="text-lg text-gray-600">Your tax document management dashboard</p>
            </div>
          </div>

          {/* Document Review Status */}
          {isPendingReview && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8"
            >
              <div className="flex items-start">
                <DocumentArrowUpIcon className="h-6 w-6 text-blue-600 mt-1 mr-3" />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-blue-800 mb-2">Document Review Required</h3>
                  <p className="text-blue-700 mb-4">
                    After you upload your documents, your business will be reviewed by our team to ensure comprehensive IRS audit protection coverage.
                  </p>
                  <div className="bg-white rounded-lg p-4 border border-blue-200">
                    <h4 className="font-semibold text-gray-900 mb-3">Required Documents for Review:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {requiredDocuments.map((doc, index) => (
                        <div key={index} className="flex items-start space-x-2">
                          <DocumentArrowUpIcon className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{doc.name}</div>
                            <div className="text-xs text-gray-600">{doc.description}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-blue-700 mt-4">
                    <strong>Next Steps:</strong> Upload your documents below and our team will review them within 1-2 business days to complete your audit protection setup.
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Review Complete - Ready to Pay */}
          {canPay && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8"
            >
              <div className="flex items-start">
                <CheckCircleIcon className="h-6 w-6 text-green-600 mt-1 mr-3" />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-green-800 mb-2">Review Complete - Ready to Pay</h3>
                  <p className="text-green-700 mb-4">
                    Your account review is complete! Your custom pricing plan has been set.
                  </p>
                  <div className="bg-white rounded-lg p-4 border border-green-200">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="text-lg font-semibold text-gray-900">{user.planName} Plan</div>
                        <div className="text-sm text-gray-600">Monthly subscription</div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-gray-900">${user.monthlyPrice}</div>
                        <div className="text-sm text-gray-600">per month</div>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handlePay}
                    className="mt-4 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium"
                  >
                    Complete Payment & Activate Protection
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div
              whileHover={{ y: -4 }}
              className="p-6 rounded-xl bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border border-white/20"
            >
              <div className="flex items-center">
                <div className="p-3 bg-blue-500/10 rounded-lg">
                  <DocumentTextIcon className="h-8 w-8 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Documents</p>
                  <p className="text-2xl font-semibold text-gray-900">{docs.length}</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              whileHover={{ y: -4 }}
              className="p-6 rounded-xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-white/20"
            >
              <div className="flex items-center">
                <div className="p-3 bg-green-500/10 rounded-lg">
                  <ShieldCheckIcon className="h-8 w-8 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Verified Documents</p>
                  <p className="text-2xl font-semibold text-gray-900">{docs.filter(d => d.verified).length}</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              whileHover={{ y: -4 }}
              className="p-6 rounded-xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-white/20"
            >
              <div className="flex items-center">
                <div className="p-3 bg-purple-500/10 rounded-lg">
                  <CreditCardIcon className="h-8 w-8 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Subscription</p>
                  <p className="text-2xl font-semibold text-gray-900 capitalize">{user.subscriptionStatus}</p>
                </div>
              </div>
            </motion.div>
          </div>
        </GlassCard>

        {/* Document Management Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <GlassCard className="p-8 mb-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">Tax Documents</h2>
              <p className="text-gray-600 mt-1">Upload and manage your tax documents</p>
            </div>
            <div className="flex space-x-3">
              {docs.length > 0 && (
                <>
                  <button
                    onClick={refreshDocs}
                    className="px-4 py-2 bg-white/80 hover:bg-white/90 rounded-xl text-gray-700 font-medium transition-all duration-200"
                    disabled={deleting}
                  >
                    Refresh
                  </button>
                  <button
                    onClick={deleteAllDocuments}
                    className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl font-medium transition-all duration-200 flex items-center"
                    disabled={deleting}
                  >
                    <TrashIcon className="h-5 w-5 mr-2" />
                    {deleting ? 'Deleting...' : 'Delete All'}
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="space-y-8">
            <DocumentUpload onUploadComplete={refreshDocs} />

            {docs.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {docs.map((d) => (
                  <DocumentCard key={d.id} doc={d} />
                ))}
              </div>
            )}
          </div>
          </GlassCard>
        </motion.div>

        {/* Subscription Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <GlassCard className="p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <CreditCardIcon className="h-6 w-6 text-indigo-600 mr-3" />
              <span className="text-xl font-semibold text-gray-900">Subscription Details</span>
            </div>
            {canPay && (
              <button onClick={handlePay} className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-all duration-200">
                Pay & Activate
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 rounded-xl bg-gradient-to-br from-indigo-500/5 to-blue-500/5">
              <p className="text-sm font-medium text-gray-600">Plan</p>
              <p className="text-xl font-semibold text-gray-900">{user.planName || 'TBD'}</p>
            </div>
            <div className="p-6 rounded-xl bg-gradient-to-br from-blue-500/5 to-cyan-500/5">
              <p className="text-sm font-medium text-gray-600">Monthly Price</p>
              <p className="text-xl font-semibold text-gray-900">
                {user.monthlyPrice ? `$${user.monthlyPrice}` : 'TBD'}
              </p>
            </div>
            <div className="p-6 rounded-xl bg-gradient-to-br from-cyan-500/5 to-teal-500/5">
              <p className="text-sm font-medium text-gray-600">Status</p>
              <p className="text-xl font-semibold text-gray-900 capitalize">
                {user.subscriptionStatus === 'pending_review' ? 'Account in Review' :
                 user.subscriptionStatus === 'review_complete' ? 'Ready to Pay' :
                 user.subscriptionStatus || 'Pending'}
              </p>
            </div>
          </div>
          
          {/* Status-specific messages */}
          {isPendingReview && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Document Review:</strong> Upload your tax documents below and our team will review them to ensure comprehensive audit protection coverage. This typically takes 1-2 business days.
              </p>
            </div>
          )}
          
          {canPay && (
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">
                <strong>Review Complete:</strong> Your custom pricing has been set. Complete payment to activate your IRS audit protection.
              </p>
            </div>
          )}
          
          </GlassCard>
        </motion.div>

        {/* Audit Summary */}
        {audit && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <GlassCard className="p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-semibold text-gray-900">Audit Risk Analysis</h3>
                <p className="text-gray-600 mt-1">AI-powered risk assessment of your tax documents</p>
              </div>
              <button
                onClick={() => {
                  api.get('/api/audit-summary?recalc=true').then(r => setAudit(r.data));
                }}
                className="px-4 py-2 bg-white/80 hover:bg-white/90 rounded-xl text-gray-700 font-medium transition-all duration-200"
              >
                Re-Analyze
              </button>
            </div>

            <div className="flex items-start space-x-8">
              <div className="flex-shrink-0 p-6 rounded-xl bg-gradient-to-br from-blue-500/5 to-indigo-500/5">
                <div className={`text-6xl font-bold ${
                  audit.auditScore > 70 ? 'text-red-600' :
                  audit.auditScore > 40 ? 'text-yellow-600' :
                  'text-green-600'
                }`}>
                  {audit.auditScore}
                </div>
                <div className="text-sm font-medium text-gray-600 mt-2">Risk Score</div>
              </div>

              <div className="flex-1 p-6 rounded-xl bg-white/50">
                <div className="prose prose-lg max-w-none">
                  {audit.aiSummary.split('.').filter(Boolean).map((sentence, i) => (
                    <p key={i} className="mb-4 text-gray-700">{sentence.trim()}.</p>
                  ))}
                </div>
              </div>
            </div>
            </GlassCard>
          </motion.div>
        )}
      </div>
    </>
  );
};

export default Dashboard; 